<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { Video, Calendar, FileText, Bell } from 'lucide-vue-next'

interface Notification {
  id: number
  type: 'video' | 'lesson' | 'material' | 'general'
  title: string
  message: string
  time: string
  read: boolean
  total_enrollments?: number
  schedule_id?: number
  date?: string
  is_active?: boolean
  lesson_links?: {
    room_id: string
    teacher_link: string
    student_link: string
    generated_at: string
  } | null
}

const props = withDefaults(defineProps<{
  notifications: Notification[]
  scheduleUrl?: string
  lessonBaseUrl?: string
}>(), {
  scheduleUrl: '/student/schedule',
  lessonBaseUrl: '/student/lessons',
})

function isSessionActive(notification: Notification): boolean {
  return !!notification.lesson_links && !!notification.is_active
}

function getLessonPageUrl(notification: Notification): string {
  const scheduleId = notification.schedule_id || notification.id
  return `${props.lessonBaseUrl}/${scheduleId}/${notification.date}`
}

function getIcon(type: string) {
  switch (type) {
    case 'video':
      return Video
    case 'lesson':
      return Calendar
    case 'material':
      return FileText
    default:
      return Bell
  }
}

function getIconClasses(type: string): { bg: string; text: string; dot: string } {
  switch (type) {
    case 'video':
      return { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' }
    case 'lesson':
      return { bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' }
    case 'material':
      return { bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' }
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500' }
  }
}

function isEmptySession(notification: Notification): boolean {
  return notification.total_enrollments !== undefined && notification.total_enrollments === 0
}
</script>

<template>
  <section>
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-lg font-bold text-gray-900">Upcoming Sessions</h2>
      <Link :href="scheduleUrl" class="text-blue-600 font-medium text-xs hover:underline">View Full Calendar</Link>
    </div>
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div v-if="notifications.length > 0" class="divide-y divide-gray-200">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="['flex items-center p-2.5 transition-colors', isEmptySession(notification) ? 'opacity-60 bg-gray-50' : 'hover:bg-gray-50']"
        >
          <Link
            :href="scheduleUrl"
            :class="['flex items-start flex-1 min-w-0 cursor-pointer']"
          >
            <div
              :class="[
                'w-7 h-7 rounded-lg flex items-center justify-center mr-2 shrink-0',
                isEmptySession(notification) ? 'bg-gray-100' : getIconClasses(notification.type).bg
              ]"
            >
              <component
                :is="getIcon(notification.type)"
                :class="['w-3.5 h-3.5', isEmptySession(notification) ? 'text-gray-400' : getIconClasses(notification.type).text]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p :class="['text-xs font-semibold', isEmptySession(notification) ? 'text-gray-400' : 'text-gray-900']">{{ notification.title }}</p>
              <p :class="['text-xs', isEmptySession(notification) ? 'text-gray-400' : 'text-gray-600']">{{ notification.message }}</p>
            </div>
          </Link>
          <div class="flex items-center gap-2 ml-2 shrink-0">
            <Link
              v-if="isSessionActive(notification)"
              :href="getLessonPageUrl(notification)"
              class="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 hover:bg-green-200 rounded px-2 py-1 transition-colors"
            >
              <Video class="w-3 h-3" />
              Join
            </Link>
            <span :class="['text-xs', isEmptySession(notification) ? 'text-gray-400' : 'text-gray-500']">{{ notification.time }}</span>
            <div
              v-if="!notification.read"
              :class="['w-1.5 h-1.5 rounded-full', isEmptySession(notification) ? 'bg-gray-300' : getIconClasses(notification.type).dot]"
            ></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="p-6 text-center">
        <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
          <Calendar class="w-5 h-5 text-gray-400" />
        </div>
        <p class="text-xs text-gray-500">No upcoming sessions</p>
      </div>
    </div>
  </section>
</template>
